import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import ExpansionBar from 'components/molecules/ExpansionBar';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import BrandValueChartContainer from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import SalesAttributionChart from 'components/organism/GrowthAnalysis/SalesAttributionChart/SalesAttributionChart';
import SalesChartContainer from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { defaultApplyFilter, from, salesDataType, salesTileItemType } from 'components/pages/GrowthAnalysis';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getGrowthAnalysisColumns } from 'utils/growthAnalysisTableColumns';
import { othersString } from 'parser/attributeSalesDataParser';
import {
    calculateContainerHeight,
    convertGlobalFilterToHtml,
    generateDefaultApplyFilter,
    getAdditionalDataOnCopyClick,
    getConfig,
    globalFilterFlattened,
} from 'utils/helperMethods';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    GROWTH_ANALYSIS_CHART_COPY_CLICKED,
    GROWTH_ANALYSIS_CHART_EXPORT_CLICKED,
    GROWTH_ANALYSIS_EXPANDED_CLICKED,
    GROWTH_ANALYSIS_RIGHT_CLICKED_ATTRIBUTE_SELECTED,
    GROWTH_ANALYSIS_TABLE_COPY_CLICKED,
    GROWTH_ANALYSIS_TABLE_EXPORT_CLICKED,
} from 'utils/mixpanelEventNames';
import TrendChart from 'components/organism/GrowthAnalysis/TrendChart';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { growthAnalysisDownload, growthAnalysisTableDownload } from 'parser/graphToExcelDownloadParser';
import SalesAttributionDrillDownChart from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { FilterContextData } from 'contexts/FilterContext';
import { colorConstants } from 'config/colorConstants';
import { tabMapping } from 'parser/attributeSalesDataParser';
import { UserContext } from 'contexts/ConfigContext';
import TrendAreaChartContainer from 'components/organism/GrowthAnalysis/TrendAreaChartContainer';
import { Switch } from 'antd';

export const height = Math.max(250, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200
export const graphItemContainerClass = 'p-4 mb-4 border border-borderLinePurpule rounded';
export const subHeaderClass = 'm-0 font-medium font-lg text-subHeaderColor';
export const yAxisLabelClass = 'text-greyFont font-xs font-medium';

interface Props {
    salesTileData: tileDataProp[];
    salesTile: salesTileItemType;
    absTile: tileDataProp;
    volTile: tileDataProp;
    index: number;
    attributeTypeItem: any;
    attributeTypeItems: any[];
    drillDownHandler: (item: any, index: number, applyFilterObj: any) => void;
    dropdownItems: any[];
    dropdownClickHandler: (item: any, index: number, applyFilterObj: any) => void;
    attrTypeOptions: any;
    salesDataset: salesDataType[];
    drillDownSelectedData: any;
    previousApplyFilter: any;
    infiniteScrollerOnNext: (index: number, width: number) => void;
    infiniteScrollSettings: any;
    stringifiedFilter: string;
    from: from;
    timePeriod: string | string[];
    comparePeriodEnabled: boolean;
    comparisonPeriodType?: string;
    selectedTimePeriodDropdownValue?: string;
    customTimePeriod?: [string, string];
    showTotals?: boolean;
}

function formatLabel(label: string[], index: number, length: number) {
    //if there are 2 attribute types, it means comparison mode is enabled and hence change 2nd label only
    if (!label?.length || index + 1 < length) return '';
    return `- ${label.join(' > ')}`;
}

const graphExportHeaderMapping = {
    promo: tabMapping.promo.tabs,
    incremental: tabMapping.incremental.tabs,
    sales: ['Sales'],
};

export const growthAnalysisTableId = 'growth-analysis-table';

const ExpandedView: FC<Props> = ({
    salesTileData,
    salesTile,
    absTile,
    volTile,
    index,
    attributeTypeItem,
    attributeTypeItems,
    drillDownHandler,
    dropdownItems,
    dropdownClickHandler,
    attrTypeOptions,
    salesDataset,
    drillDownSelectedData,
    infiniteScrollerOnNext,
    infiniteScrollSettings,
    stringifiedFilter,
    previousApplyFilter,
    from,
    timePeriod,
    comparePeriodEnabled,
    comparisonPeriodType,
    selectedTimePeriodDropdownValue,
    customTimePeriod,
    showTotals,
}) => {
    const [expandedItem, setExpandedItem] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [isTableExpanded, setIsTableExpanded] = useState(attributeTypeItems?.length < 2);
    const [selectedAttribute, setSelectedAttribute] = useState('');
    const [selectedSecondChartItem, setSelectedSecondChartItem] = useState<any>(null);
    const [firstChartItem, setFirstChartItem] = useState<any>(null);
    const [selectPPG, setSelectPPG] = useState<any>(null);
    const [attributeTypesList, setAttributeTypesList] = useState<any[]>([]);
    const { allCheckedAttribute, idForRestOfComponent, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const showFullLabel = commonConfig?.showFullLabel;
    const graphExportHeaderMapping = growthAnalysisConfig?.graphExportHeaderMapping;
    const refSalesChartContainer = useRef(null);
    const refTable = useRef(null);
    const isDepletion = salesTile?.key === 'depletion';
    const comparePeriodLabel =
        selectedTimePeriodDropdownValue === 'custom'
            ? `${customTimePeriod?.[0]} - ${customTimePeriod?.[1]}`
            : selectedTimePeriodDropdownValue === 'prior_period'
            ? 'PP'
            : 'YA';

    const headerLabels = useMemo(() => {
        const subHeaderLabel = isDepletion //for depletion
            ? `${attributeTypeItem?.selectedAttributeType} - Sales Change vs YA ${formatLabel(
                  previousApplyFilter.labels,
                  index,
                  attributeTypeItems.length,
              )}`
            : salesTile.key === salesTileData[0].key
            ? `${attributeTypeItem?.selectedAttributeType} Sales vs ${comparePeriodLabel} ${formatLabel(
                  previousApplyFilter.labels,
                  index,
                  attributeTypeItems.length,
              )}`
            : `${attributeTypeItem?.selectedAttributeType} - ${absTile.label} vs ${comparePeriodLabel} ${formatLabel(
                  previousApplyFilter.labels,
                  index,
                  attributeTypeItems.length,
              )}`;
        const tableHeader = `${attributeTypeItem?.selectedAttributeType} ${
            absTile.label
        } vs ${comparePeriodLabel} Table ${formatLabel(previousApplyFilter.labels, index, attributeTypeItems.length)}`;
        return { subHeaderLabel, tableHeader };
    }, [
        salesTile,
        absTile,
        attributeTypeItem?.selectedAttributeType,
        salesDataset[0].chartData,
        salesDataset[1].chartData,
    ]);

    const stringifiedGlobalFilter = useRef<string>(stringifiedFilter);

    const {
        data: applyFilterData,
        loading: applyFilterLoading,
        error: applyFilterError,
        applyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete, onError: onApplyFilterError });

    //reset expanded item whenever global filter changes
    useEffect(() => {
        stringifiedGlobalFilter.current = stringifiedFilter;
        return () => {
            setExpandedItem(null);
        };
    }, [stringifiedFilter]);

    useEffect(() => {
        if (expandedItem) {
            prepareApplyFilterVariableAndExecute();
        }
    }, [expandedItem]);

    useEffect(() => {
        if (volTile.key === 'profit' && expandedItem) setExpandedItem(null); //clear expanded item when dropdown value is profit
        setSelectedAttribute('');
    }, [volTile]);

    useEffect(() => {
        setSelectedAttribute('');
    }, [expandedItem]);

    // resetting sales attribution drilldown when time period changes
    useEffect(() => {
        if (selectedAttribute) {
            setSelectedAttribute('');
        }
    }, [timePeriodSelection]);

    function onApplyFilterError() {
        setLoading(false);
    }

    function prepareApplyFilterVariableAndExecute() {
        const defaultApplyFilterGenerated = generateDefaultApplyFilter(idForRestOfComponent);
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilterGenerated);
        if (expandedItem.dimension === 'dim1') {
            tempApplyFilterVariable.jsonstring_dim1.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        } else if (expandedItem.dimension === 'dim2') {
            tempApplyFilterVariable.jsonstring_dim2.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        } else {
            tempApplyFilterVariable.jsonstring_dim3.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        }
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        tempApplyFilterVariable.jsonstring_dim3 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim3);
        tempApplyFilterVariable.jsonstring_dim4 = JSON.stringify(tempApplyFilterVariable?.jsonstring_dim4 || '{}');
        applyFilterMutate({ variables: tempApplyFilterVariable });
    }

    //called when apply filter call has completed
    function onApplyFilterComplete(payload: any) {
        const idForRestOfComponent = JSON.parse(stringifiedFilter);
        //const tempApplyFilterResponse = _.cloneDeep(JSON.parse(dummyVariables.filters));
        const tempApplyFilterResponse = _.cloneDeep(idForRestOfComponent);
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        let commonIdsDim3 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (idForRestOfComponent.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(idForRestOfComponent.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (idForRestOfComponent.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(idForRestOfComponent.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        if (payload?.dim3?.ids?.length > 0) {
            commonIdsDim3 = [...payload.dim3.ids];
            if (tempApplyFilterResponse.dim3?.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim3 = _.intersection(tempApplyFilterResponse.dim3.ids, payload.dim3.ids);
            }
            tempApplyFilterResponse.dim3.ids = [...commonIdsDim3];
        }
        const stringifiedApplyFilterResponse = JSON.stringify(tempApplyFilterResponse);
        stringifiedGlobalFilter.current = stringifiedApplyFilterResponse;
        setLoading(false);
    }

    //called when infinite scrolling
    const infiniteScrollerNext = (index: number, width: number = 0) => {
        //console.log('logging index', index);
        infiniteScrollerOnNext(index, width);
    };
    //called when clicked on explore more icon on hovering sales graph
    const exploreMoreClickHandler = (item: any) => {
        const key = 'category';
        if (salesDataset[0].loading || salesDataset[1].loading) return;
        if (item[0]?.[key]?.trim() === othersString) return;
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        if (_.isEqual(applyFilterItem, expandedItem)) setExpandedItem(null);
        else {
            setLoading(true);
            //setSelectedAttribute('structural_demand');
            setExpandedItem({ ...applyFilterItem });
        }
    };

    //called when right clicked on sales graph
    const rightClickHandler = (item: any, attrType: any, index: number) => {
        const key = 'category';
        if (item[0]?.[key]?.trim() === othersString) return;
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        dropdownClickHandler(attrType, index, applyFilterItem);
        //MIXPANEL EVENT
        trackEventMixpanel(GROWTH_ANALYSIS_RIGHT_CLICKED_ATTRIBUTE_SELECTED, {
            selectedAttributeType: attrType?.label,
            selectedAttributeName: item?.[0]?.category,
        });
    };

    //called when clicked on bar item on hovering sales graph
    const drillDownClickHandler = (item: any) => {
        const key = 'category';
        if (salesDataset[0].loading || salesDataset[1].loading) return;
        if (item[0]?.[key]?.trim() === othersString) return;
        const applyFilterItem = {
            attributeExId: item[0]?.[key],
            attributeDesc: item[0]?.[key],
            dimension: attributeTypeItem.dimension,
            attributeType: _.snakeCase(attributeTypeItem.selectedAttributeType),
        };
        // if (key in drillDownItem && item[0]?.[key] === drillDownItem?.[key]) setDrillDownItem({}); // undo/reset action
        // else setDrillDownItem(item[0]); //setting action
        drillDownHandler(
            { label: attributeTypeItem?.selectedAttributeType, value: attributeTypeItem?.selectedAttributeValue },
            attrTypeOptions.data?.length - dropdownItems?.length,
            applyFilterItem,
        );
    };
    //table columns
    const memoisedTableColumns = useMemo(() => {
        if (salesDataset[index].tableData?.length > 0) {
            return getGrowthAnalysisColumns(
                salesDataset[index].tableData,
                volTile,
                salesTile.key,
                expandedItem,
                drillDownSelectedData,
                commonConfig,
                !showFullLabel,
                showTotals,
            );
        }
    }, [salesDataset, volTile, salesTile, expandedItem, showTotals]);

    //reset expanded item when sales tile is changed
    useEffect(() => {
        if (expandedItem) setExpandedItem(null);
    }, [salesTile]);
    //header for table export
    const excelHeader = [
        attributeTypeItem?.selectedAttributeType,
        ...(salesDataset[index]?.tableData?.map((item: any) => {
            return item.type;
        }) || []),
    ];

    //trends chart label
    const trendsChartLabel = `${firstChartItem?.category} - ${selectedSecondChartItem?.category}`;
    const [showGroupedChartLabel, setShowGroupedChartLabel] = useState(false);
    return (
        <div style={{ width: '100%' }}>
            <div className={`${graphItemContainerClass}`} ref={refSalesChartContainer}>
                <div className="flex items-center gap-1 justify-between">
                    <p className={subHeaderClass}>{headerLabels.subHeaderLabel}</p>
                    <span className="flex items-center gap-2">
                        {/* SHOW LABEL TOGGLE FOR GROUPED CHART */}
                        {!(salesTile.key === salesTileData[0].key || isDepletion) && (
                            <div className="mx-2">
                                <span className="font-xs text-blackAlt mr-1">Show Label</span>
                                <Switch
                                    onChange={(val) => setShowGroupedChartLabel(val)}
                                    checked={showGroupedChartLabel}
                                />
                            </div>
                        )}

                        <CopyToClipBoard
                            removeScrollbar
                            mixpanelEvent={() => trackEventMixpanel(GROWTH_ANALYSIS_CHART_COPY_CLICKED)}
                            refOfComponent={refSalesChartContainer}
                        />
                        {/* Header hardCoded for now */}
                        <DownloadExcel
                            mixpanelEvent={() => trackEventMixpanel(GROWTH_ANALYSIS_CHART_EXPORT_CLICKED)}
                            title={growthAnalysisTableId}
                            excelData={growthAnalysisDownload(salesDataset[index], salesTile)}
                            headers={[
                                attributeTypeItem?.selectedAttributeType,
                                ...graphExportHeaderMapping[salesTile.key],
                            ]}
                        />
                    </span>
                </div>
                {/* SALES CHART AND INCREMENTAL/PROMO CHART */}
                {salesTile.key === salesTileData[0].key || isDepletion ? (
                    <SalesChartContainer
                        infiniteScrollSettings={infiniteScrollSettings}
                        infiniteScrollerNext={infiniteScrollerNext}
                        absTile={absTile}
                        volTile={volTile}
                        data={salesDataset[index]}
                        attributeTypeItem={attributeTypeItem}
                        attrTypeOptions={attrTypeOptions}
                        disableItemHover={
                            attributeTypeItems?.length > 1 ? attributeTypeItems?.length - 1 === index : false
                        }
                        disableDrillDownHover={attributeTypeItem?.disableHover}
                        disableRightClick={attributeTypeItems?.length > 1}
                        dropdownItems={dropdownItems}
                        drillDownOnClick={drillDownClickHandler}
                        index={index}
                        expandedItem={expandedItem}
                        drilledDownItem={drillDownSelectedData}
                        exploreMoreOnClick={exploreMoreClickHandler}
                        dropdownClickHandler={rightClickHandler}
                        isTableExpanded={isTableExpanded}
                    />
                ) : (
                    <BrandValueChartContainer
                        showGroupedChartLabel={showGroupedChartLabel}
                        absTile={absTile}
                        salesTile={salesTile}
                        data={salesDataset[index]}
                        index={index}
                        volTile={volTile}
                        infiniteScrollSettings={infiniteScrollSettings}
                        infiniteScrollerNext={infiniteScrollerNext}
                    />
                )}
                {!salesDataset[index].loading && !salesDataset[index].error && salesDataset[index].chartData?.data && (
                    <p className={`text-center !top-[-35px] mb-[-35px] relative ${yAxisLabelClass}`}>
                        {attributeTypeItem?.selectedAttributeType}
                    </p>
                )}
            </div>
            {expandedItem &&
                (salesTile.key === salesTileData[0].key ? (
                    <div>
                        {/* ATTRIBUTION CHART COMPONENT */}
                        <div className={`${graphItemContainerClass} !px-0 !pb-0`}>
                            <SalesAttributionChart
                                expandedItem={expandedItem}
                                onClick={setSelectedAttribute}
                                selected={selectedAttribute}
                                showTileButtons={false}
                                index={index}
                                stringifiedFilter={stringifiedGlobalFilter.current}
                                volTile={volTile}
                                loading={loading}
                                timePeriod={timePeriod}
                                comparePeriodEnabled={comparePeriodEnabled}
                                comparisonPeriodType={comparisonPeriodType}
                                comparePeriodLabel={comparePeriodLabel}
                            />
                        </div>
                        {/* SALES ATTRIBUTION DRILLDOWN CHART COMPONENT */}
                        {selectedAttribute && volTile.key !== 'profit' && (
                            <SalesAttributionDrillDownChart
                                attributeTypesList={attributeTypesList}
                                setAttributeTypesList={setAttributeTypesList}
                                selectPPG={selectPPG}
                                setSelectPPG={setSelectPPG}
                                stringifiedFilter={stringifiedGlobalFilter.current}
                                volTile={volTile}
                                firstChartItem={firstChartItem}
                                setFirstChartItem={setFirstChartItem}
                                expandedItem={expandedItem}
                                selectedAttribute={selectedAttribute}
                                secondChartItem={selectedSecondChartItem}
                                setSecondChartItem={setSelectedSecondChartItem}
                                timePeriod={timePeriod}
                                comparePeriodEnabled={comparePeriodEnabled}
                                comparisonPeriodType={comparisonPeriodType}
                                comparePeriodLabel={comparePeriodLabel}
                            />
                        )}
                        {/* CURRENT YEAR SALES VS YEAR AGO TREND CHART COMPONENT */}
                        {/* HIDE TREND CHART FOR PROFIT TAB AND WHEN 2ND SELECTED ITEM IS NOT PRESENT */}

                        {volTile.key !== 'profit' && selectedAttribute && selectedSecondChartItem && (
                            <TrendChart
                                chart2Item={selectedSecondChartItem}
                                chart1Item={firstChartItem}
                                selectPPG={selectPPG}
                                label={trendsChartLabel}
                                absTile={absTile}
                                volTile={volTile}
                                loading={loading}
                                index={index}
                                stringifiedFilter={stringifiedGlobalFilter.current}
                                timePeriod={timePeriod}
                                comparisonPeriodType={comparisonPeriodType}
                                customTimePeriod={customTimePeriod}
                            />
                        )}
                    </div>
                ) : isDepletion ? (
                    <div>
                        <TrendAreaChartContainer
                            expandedItem={expandedItem}
                            index={index}
                            loading={loading}
                            stringifiedFilter={stringifiedGlobalFilter.current}
                            volTile={volTile}
                            applyFilterError={applyFilterError}
                        />
                    </div>
                ) : null)}

            <div
                id={`${growthAnalysisTableId}-${index}`}
                className={`${index < attributeTypeItems?.length - 1 ? 'mb-4' : ''}`}
            >
                {attributeTypeItems?.length > 1 && (
                    <div style={{ marginBottom: index === 1 ? '30px' : '0px' }}>
                        <ExpansionBar
                            title={headerLabels.tableHeader}
                            isExpanded={isTableExpanded}
                            onClick={(visible: boolean) => {
                                setIsTableExpanded(visible);

                                //mixpanel event
                                trackEventMixpanel(GROWTH_ANALYSIS_EXPANDED_CLICKED, { isTableExpanded: visible });
                            }}
                        />
                    </div>
                )}
                {isTableExpanded && (
                    <>
                        <div className="flex justify-end mb-2 mt-4">
                            <span className="flex gap-2">
                                <CopyToClipBoard
                                    elementClassToRemoveScrollbar="ant-table-body"
                                    removeScrollbar
                                    mixpanelEvent={() => trackEventMixpanel(GROWTH_ANALYSIS_TABLE_COPY_CLICKED)}
                                    refOfComponent={refTable}
                                />

                                <DownloadExcel
                                    mixpanelEvent={() => trackEventMixpanel(GROWTH_ANALYSIS_TABLE_EXPORT_CLICKED)}
                                    title={'growthAnalysisTable'}
                                    excelData={growthAnalysisTableDownload(salesDataset[index].tableData, volTile)}
                                    headers={excelHeader}
                                />
                            </span>
                        </div>
                        <div ref={refTable}>
                            <GrowthAnalysisTable
                                tableLoading={salesDataset[index].loading}
                                columns={memoisedTableColumns}
                                data={salesDataset[index].tableData}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default React.memo(ExpandedView);
